import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Armchair, AlertCircle, RefreshCw } from "lucide-react";

/**
 * Visual seat map rendered from Amadeus or Travelport SeatMap responses.
 * Both suppliers return the SAME shape: { data: [{decks:[{seats:[{number, characteristicsCodes, travelerPricing:[]}]}]}] }
 * Props:
 *  - flightOffer: Amadeus offer (raw) — used when supplier !== "travelport"
 *  - travelportFlight: full Travelport flight object (with travelport_segments) — used when supplier === "travelport"
 *  - supplier: "amadeus" | "travelport"
 *  - segmentIndex: which segment of the offer (0,1,...)
 *  - selected: seat number (string like "12A") or null
 *  - onSelect: callback(seatNumber, priceUSD)
 */
const safeString = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  // never let an object escape into JSX
  try { return JSON.stringify(v); } catch { return "Unavailable"; }
};

export default function SeatMapView({ flightOffer, travelportFlight, supplier = "amadeus", segmentIndex = 0, selected, onSelect }) {
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const { format: fmt } = useCurrency();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErrMsg("");
      try {
        const isTP = supplier === "travelport";
        const url = isTP ? "/travelport/seatmap" : "/flights/seatmap";
        const body = isTP ? { flight: travelportFlight } : { flight_offer: flightOffer };
        const { data } = await api.post(url, body);
        if (!alive) return;
        if (data?.available === false) {
          setErrMsg(safeString(data.reason) || "Seat map not available for this flight.");
          setResp(null);
        } else {
          setResp(data);
        }
      } catch (e) {
        if (!alive) return;
        const d = e?.response?.data;
        const msg = (d && (d.reason || d.detail)) || e?.message || "Seat map not available for this flight.";
        setErrMsg(safeString(msg));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [flightOffer, travelportFlight, supplier]);

  // Build the seat-map model (rows + columns) from the Amadeus payload
  const model = useMemo(() => {
    if (!resp) return null;
    const seatmaps = resp.data || [];
    const sm = seatmaps[segmentIndex] || seatmaps[0];
    if (!sm) return null;
    const deck = (sm.decks || [])[0] || {};
    const seatList = deck.seats || [];

    const rowMap = new Map(); // row# -> { col: seat }
    const colSet = new Set();
    seatList.forEach((s) => {
      const num = s.number || "";
      const rowMatch = num.match(/\d+/);
      const colMatch = num.match(/[A-Z]+$/);
      const row = rowMatch ? rowMatch[0] : (s.coordinates?.x ?? "?");
      const col = colMatch ? colMatch[0] : (s.coordinates?.y ?? "?");
      colSet.add(col);
      if (!rowMap.has(row)) rowMap.set(row, {});
      rowMap.get(row)[col] = s;
    });
    const cols = Array.from(colSet).sort();
    const rows = Array.from(rowMap.keys()).sort((a, b) => Number(a) - Number(b));

    // Determine aisle position: gap between columns. For a typical 3-3 cabin
    // we insert an aisle between C and D (i.e. half-way). Use a generic split.
    const aisleAfterIdx = Math.floor(cols.length / 2) - 1;

    return {
      aircraftCode: sm.aircraftCode || sm.aircraft?.code || "",
      cabin: deck.deckType || (sm.class || "Cabin"),
      rows: rows.map((r) => ({
        row: r,
        seats: cols.map((c) => rowMap.get(r)[c] || null),
      })),
      cols,
      aisleAfterIdx,
      currency: resp?.dictionaries?.currencies ? Object.keys(resp.dictionaries.currencies)[0] : "USD",
    };
  }, [resp, segmentIndex]);

  if (loading) {
    return (
      <div data-testid="seatmap-loading" className="text-sm text-slate-500 py-8 text-center inline-flex items-center justify-center gap-2 w-full">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading seat map…
      </div>
    );
  }
  if (errMsg) {
    return (
      <div data-testid="seatmap-unavailable" className="flex items-start gap-2 p-4 rounded-lg bg-amber-50 border border-amber-200">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-amber-800">Seat map not available</div>
          <div className="text-xs text-amber-700 mt-1">{errMsg}</div>
          <div className="text-xs text-slate-500 mt-2">
            You can still proceed — seat assignment will be done at airport check-in by the carrier.
          </div>
        </div>
      </div>
    );
  }
  if (!model || model.rows.length === 0) {
    return <div data-testid="seatmap-empty" className="text-sm text-slate-500 py-6 text-center">No seats published for this segment.</div>;
  }

  const priceOf = (s) => {
    const tp = (s?.travelerPricing || [])[0];
    const v = tp?.price?.total;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const isAvail = (s) => {
    const status = ((s?.travelerPricing || [])[0]?.seatAvailabilityStatus || "").toUpperCase();
    return status === "AVAILABLE";
  };
  const isCharac = (s, code) => (s?.characteristicsCodes || []).includes(code);

  // Legend
  return (
    <div data-testid="seatmap-root">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2 text-xs text-slate-500">
        <span><span className="font-mono">{safeString(model.aircraftCode) || "Cabin"}</span> · {safeString(model.cabin)}</span>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-200 border border-emerald-400" /> Available</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-200 border border-amber-400" /> Extra-legroom</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-200 border border-slate-300" /> Taken</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[var(--brand-primary)]" /> Selected</span>
        </div>
      </div>

      {/* Aircraft outline */}
      <div className="relative mx-auto max-w-md">
        <div className="border-2 border-slate-300 rounded-t-[40px] rounded-b-2xl bg-gradient-to-b from-slate-50 to-white p-3 pt-6">
          <div className="text-center text-[10px] text-slate-400 mb-2">↑ Front of plane ↑</div>

          {/* Column headers */}
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <div className="w-6" />
            {model.cols.map((c, i) => (
              <React.Fragment key={c}>
                <div className="w-7 text-center text-[10px] font-semibold text-slate-400">{c}</div>
                {i === model.aisleAfterIdx && <div className="w-3" />}
              </React.Fragment>
            ))}
            <div className="w-6" />
          </div>

          <div className="overflow-auto max-h-[420px] pr-1 space-y-1.5">
            {model.rows.map(({ row, seats }) => (
              <div key={row} className="flex items-center justify-center gap-1.5">
                <div className="w-6 text-[10px] text-slate-400 text-right">{row}</div>
                {seats.map((s, ci) => {
                  const colChar = model.cols[ci];
                  const aisleAfter = ci === model.aisleAfterIdx;
                  const cell = (() => {
                    if (!s) return <div className="w-7 h-7" />;
                    const num = safeString(s.number);
                    const avail = isAvail(s);
                    const price = priceOf(s);
                    const sel = safeString(selected) === num;
                    const extraLegroom = isCharac(s, "L") || isCharac(s, "K");
                    const exit = isCharac(s, "E");
                    const cls =
                      sel ? "bg-[var(--brand-primary)] text-white ring-2 ring-[var(--brand-primary-dark)] shadow" :
                      !avail ? "bg-slate-200 text-slate-400 cursor-not-allowed" :
                      extraLegroom ? "bg-amber-100 text-amber-800 hover:bg-amber-200" :
                      "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";
                    return (
                      <button
                        type="button"
                        disabled={!avail}
                        title={`${num}${avail ? ` · ${price > 0 ? fmt(price) : "Free"}${extraLegroom ? " · extra legroom" : ""}${exit ? " · exit row" : ""}` : " · unavailable"}`}
                        onClick={() => avail && onSelect?.(num, price)}
                        data-testid={`seat-${num}`}
                        className={`w-7 h-7 text-[9px] font-bold rounded flex items-center justify-center transition-all border ${cls}`}
                      >
                        {safeString(colChar)}
                      </button>
                    );
                  })();
                  return (
                    <React.Fragment key={`${row}-${ci}`}>
                      {cell}
                      {aisleAfter && <div className="w-3 text-[8px] text-slate-300 text-center select-none">·</div>}
                    </React.Fragment>
                  );
                })}
                <div className="w-6" />
              </div>
            ))}
          </div>

          <div className="text-center text-[10px] text-slate-400 mt-2">↓ Rear of plane ↓</div>
        </div>
      </div>

      {selected && (
        <div data-testid="seatmap-selected" className="mt-3 text-sm text-[var(--brand-accent)] flex items-center gap-2">
          <Armchair className="w-4 h-4 text-[var(--brand-primary)]" />
          Selected: <span className="font-bold">{safeString(selected)}</span>
          <Button size="sm" variant="ghost" className="ml-2 text-red-600" onClick={() => onSelect?.(null, 0)}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );
}
